import { Clapperboard, Image as ImageIcon, LayoutGrid, ListChecks, Package, Search, Sparkles, type LucideIcon } from 'lucide-react';
import { ToolCardKind } from '../../../types/chat';

export interface ToolCardMeta {
  icon: LucideIcon;
  /** 左端のアクセント線（Tailwind は静的クラス名でないと出力されない） */
  accent: string;
  /** アイコンの丸背景 */
  iconWrap: string;
}

const BY_KIND: Record<ToolCardKind, ToolCardMeta> = {
  info: { icon: Search, accent: 'bg-slate-300', iconWrap: 'bg-slate-100 text-slate-600' },
  image: { icon: ImageIcon, accent: 'bg-indigo-400', iconWrap: 'bg-indigo-50 text-indigo-600' },
  video: { icon: Clapperboard, accent: 'bg-violet-400', iconWrap: 'bg-violet-50 text-violet-600' },
  carousel: { icon: LayoutGrid, accent: 'bg-teal-400', iconWrap: 'bg-teal-50 text-teal-600' },
  proposal: { icon: Sparkles, accent: 'bg-amber-400', iconWrap: 'bg-amber-50 text-amber-600' },
  choices: { icon: ListChecks, accent: 'bg-violet-400', iconWrap: 'bg-violet-50 text-violet-600' },
  // チャットv2 のブロック種別。専用カード（chat-v2/blocks）で描くが、未登録時は従来カードで出るため既定の見た目を持たせる
  storyboard: { icon: Clapperboard, accent: 'bg-violet-400', iconWrap: 'bg-violet-50 text-violet-600' },
  ugc_cast: { icon: Clapperboard, accent: 'bg-violet-400', iconWrap: 'bg-violet-50 text-violet-600' },
  video_edit: { icon: Clapperboard, accent: 'bg-violet-400', iconWrap: 'bg-violet-50 text-violet-600' },
  auto_run: { icon: Sparkles, accent: 'bg-indigo-400', iconWrap: 'bg-indigo-50 text-indigo-600' },
  approval: { icon: Sparkles, accent: 'bg-amber-400', iconWrap: 'bg-amber-50 text-amber-600' },
  cuts: { icon: Clapperboard, accent: 'bg-violet-400', iconWrap: 'bg-violet-50 text-violet-600' },
  material: { icon: Search, accent: 'bg-slate-300', iconWrap: 'bg-slate-100 text-slate-600' },
};

const ERROR_META: ToolCardMeta = { icon: Search, accent: 'bg-rose-400', iconWrap: 'bg-rose-50 text-rose-600' };

const PRODUCT_TOOLS = new Set(['register_product_from_url', 'get_product_fetch_progress', 'list_products', 'get_product']);

/** 生成系（クレジット消費）ツール。失敗時に「同じ設定でもう一度」を出す対象。 */
export const GENERATION_TOOLS = new Set(['generate_banner', 'generate_video', 'make_banner_video', 'generate_carousel']);

export const VIDEO_MODEL_LABELS: Record<string, string> = {
  seedance: 'Seedance 1.5',
  'seedance-2-5': 'Seedance 2.5',
  'seedance-2-pro': 'Seedance 2.0 Pro',
  'seedance-2-fast': 'Seedance 2.0 Fast',
  'seedance-2-mini': 'Seedance 2.0 Mini',
  'minimax-h3': 'MiniMax H3',
  'fal-h3-max': 'H3 Max',
  'fal-h3-max-turbo': 'H3 Max Turbo',
  gemini: 'Veo (Gemini)',
};

export function toolCardMeta(name: string, kind: ToolCardKind, isError: boolean): ToolCardMeta {
  const base = BY_KIND[kind];
  if (isError) return { ...ERROR_META, icon: base.icon };
  if (kind === 'info' && PRODUCT_TOOLS.has(name)) return { ...base, icon: Package };
  if (kind === 'proposal') {
    const icon = name.includes('video') ? Clapperboard : name.includes('carousel') ? LayoutGrid : ImageIcon;
    return { ...base, icon };
  }
  return base;
}
